import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import UserDetailModal from '../../shared/components/UserDetailModal';
import { useAuth } from '../auth/AuthContext';
import { useUserNames, formatUserLabel } from '../../shared/hooks/useUserNames';
import { extractErrorMessage } from '../../shared/utils/errors';
import { DisputesFilters, getDisputes } from './api';
import { getP2pSettings, updateP2pSettings } from './settingsApi';
import { DisputeRecord, DisputePriority, DisputeStatus, PaginatedResponse } from '../../shared/types';

type WindowUnit = 'minutes' | 'hours' | 'days';

const UNIT_MINUTES: Record<WindowUnit, number> = { minutes: 1, hours: 60, days: 1440 };

const MAX_WINDOW_MINUTES = 43200; // 30 days

const splitWindow = (minutes: number): { value: string; unit: WindowUnit } => {
  if (minutes > 0 && minutes % 1440 === 0) return { value: String(minutes / 1440), unit: 'days' };
  if (minutes > 0 && minutes % 60 === 0) return { value: String(minutes / 60), unit: 'hours' };
  return { value: String(minutes), unit: minutes > 0 ? 'minutes' : 'hours' };
};

const formatWindowLabel = (minutes: number): string => {
  if (minutes <= 0) return 'no time limit';
  const { value, unit } = splitWindow(minutes);
  return `${value} ${unit}`;
};

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Active (open + review)' },
  { value: 'open', label: 'Open' },
  { value: 'awaiting_response', label: 'Awaiting response' },
  { value: 'under_review', label: 'Under review' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const priorityBadgeClass = (p: DisputePriority): string => {
  switch (p) {
    case 'critical':
      return 'bg-red-500/15 text-red-400 border border-red-500/30';
    case 'high':
      return 'bg-orange-500/15 text-orange-300 border border-orange-500/30';
    case 'medium':
      return 'bg-amber-500/15 text-amber-300 border border-amber-500/30';
    default:
      return 'bg-slate-500/15 text-slate-300 border border-slate-500/30';
  }
};

const statusBadgeClass = (s: DisputeStatus): string => {
  switch (s) {
    case 'resolved':
      return 'badge-success';
    case 'cancelled':
      return 'badge-danger';
    case 'under_review':
      return 'badge-info';
    case 'awaiting_response':
      return 'badge-warning';
    default:
      return 'badge-warning';
  }
};

const reasonLabel = (code: string): string => {
  const map: Record<string, string> = {
    payment_not_received: 'Payment not received',
    partial_payment: 'Partial payment',
    wrong_amount: 'Wrong amount',
    fake_proof: 'Fake payment proof',
    third_party_payment: 'Third-party payment',
    blocked_funds: 'Blocked/illegal funds',
    assets_not_released: 'Assets not released',
    counterparty_unresponsive: 'Counterparty unresponsive',
    other: 'Other',
  };
  return map[code] ?? code;
};

type Props = { mode: 'p2p' | 'otc' };

const DisputesPage: React.FC<Props> = ({ mode }) => {
  const isOtc = mode === 'otc';
  const disputesBase = isOtc ? '/otc/disputes' : '/p2p/disputes';
  const { admin } = useAuth();
  const [data, setData] = useState<PaginatedResponse<DisputeRecord> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<DisputesFilters>({});
  const [userIdInput, setUserIdInput] = useState('');
  const [partyUserId, setPartyUserId] = useState<number | null>(null);
  const [partyTitle, setPartyTitle] = useState('');

  const canManageSettings =
    admin?.role === 'super_admin' ||
    admin?.backoffice_role === 'administrator' ||
    (admin?.permissions?.includes('p2p.manage_settings') ?? false);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [currentWindowMinutes, setCurrentWindowMinutes] = useState<number | null>(null);
  const [windowValue, setWindowValue] = useState('0');
  const [windowUnit, setWindowUnit] = useState<WindowUnit>('hours');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsBanner, setSettingsBanner] = useState<string | null>(null);

  const openSettings = async () => {
    setIsSettingsOpen(true);
    setSettingsError(null);
    setSettingsBanner(null);
    setSettingsLoading(true);
    try {
      const settings = await getP2pSettings();
      const minutes = (isOtc ? settings.otc_dispute_open_window_minutes : settings.dispute_open_window_minutes) ?? 0;
      setCurrentWindowMinutes(minutes);
      const { value, unit } = splitWindow(minutes);
      setWindowValue(value);
      setWindowUnit(unit);
    } catch (err) {
      setSettingsError(extractErrorMessage(err, 'Failed to load dispute settings'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsBanner(null);

    const parsed = Number(windowValue);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setSettingsError('The window must be a whole non-negative number');
      return;
    }
    const minutes = parsed * UNIT_MINUTES[windowUnit];
    if (minutes > MAX_WINDOW_MINUTES) {
      setSettingsError('The window cannot exceed 30 days');
      return;
    }

    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const updated = isOtc
        ? await updateP2pSettings({ otc_dispute_open_window_minutes: minutes })
        : await updateP2pSettings({ dispute_open_window_minutes: minutes });
      const savedMinutes = (isOtc ? updated.otc_dispute_open_window_minutes : updated.dispute_open_window_minutes) ?? minutes;
      setCurrentWindowMinutes(savedMinutes);
      setSettingsBanner('Settings saved');
    } catch (err) {
      setSettingsError(extractErrorMessage(err, 'Failed to save dispute settings'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const openParty = (userId: number, label: string) => {
    setPartyUserId(userId);
    setPartyTitle(label);
  };

  const partyIds: number[] = [];
  if (data?.data) {
    for (const d of data.data) {
      partyIds.push(d.opened_by_id, d.against_user_id);
    }
  }
  const userNames = useUserNames(partyIds);

  const load = useCallback(async (page: number, filtersToUse: DisputesFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getDisputes({
        ...filtersToUse,
        mode,
        page,
        per_page: 20,
      });
      setData(res);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load disputes'
        : 'Failed to load disputes';
      setError(msg);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  const applyUserIdFilter = () => {
    const userId = userIdInput.trim();
    setFilters((prev) => ({ ...prev, user_id: userId || undefined }));
    setCurrentPage(1);
  };

  const clearUserIdFilter = () => {
    setUserIdInput('');
    setFilters((prev) => {
      const next = { ...prev };
      delete next.user_id;
      return next;
    });
    setCurrentPage(1);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{isOtc ? 'OTC' : 'P2P'} Disputes</h1>
            <p className="text-slate-400 mt-1">
              {isOtc
                ? 'Appeals on OTC cash trades — check whether the seller entered the release code'
                : 'Appeals on P2P trades — mediate between buyer and seller'}
            </p>
          </div>
          {canManageSettings && (
            <button type="button" onClick={openSettings} className="btn btn-secondary py-2 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 0 1 1.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.894.149c-.424.07-.764.383-.929.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 0 1-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.398.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 0 1-.12-1.45l.527-.737c.25-.35.272-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 0 1 .12-1.45l.773-.773a1.125 1.125 0 0 1 1.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              Dispute settings
            </button>
          )}
        </div>

        <div className="card p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={filters.status ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFilters((prev) => {
                  const next = { ...prev };
                  if (value) next.status = value;
                  else delete next.status;
                  return next;
                });
                setCurrentPage(1);
              }}
              className="input py-2 w-56"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'active'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Priority</label>
            <select
              value={filters.priority ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFilters((prev) => {
                  const next = { ...prev };
                  if (value) next.priority = value;
                  else delete next.priority;
                  return next;
                });
                setCurrentPage(1);
              }}
              className="input py-2 w-40"
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyUserIdFilter())}
                placeholder="e.g. 123"
                className="input py-2 w-32"
              />
              <button type="button" onClick={applyUserIdFilter} className="btn btn-secondary py-2">Apply</button>
              {filters.user_id && (
                <button
                  type="button"
                  onClick={clearUserIdFilter}
                  className="btn btn-secondary py-2"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !data?.data?.length ? (
            <div className="p-12 text-center text-slate-400">No disputes found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">#</th>
                      <th className="table-header">Trade</th>
                      <th className="table-header">Reason</th>
                      <th className="table-header">Opened by</th>
                      <th className="table-header">Priority</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Amount</th>
                      <th className="table-header">Created</th>
                      <th className="table-header text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {data.data.map((d) => {
                      const buyerId =
                        d.opener_role === 'buyer' ? d.opened_by_id : d.against_user_id;
                      const sellerId =
                        d.opener_role === 'seller' ? d.opened_by_id : d.against_user_id;
                      const buyerLabel = formatUserLabel(buyerId, userNames);
                      const sellerLabel = formatUserLabel(sellerId, userNames);
                      const openerLabel = formatUserLabel(d.opened_by_id, userNames);
                      return (
                      <tr key={d.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{d.id}</td>
                        <td className="table-cell">
                          <div className="text-sm">
                            <p>Trade #{d.trade_id}</p>
                            <p className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                              <button
                                type="button"
                                onClick={() => openParty(buyerId, `Buyer ${buyerLabel}`)}
                                className="text-emerald-400 hover:text-emerald-300 hover:underline"
                                title={`Buyer #${buyerId}`}
                              >
                                B: {buyerLabel}
                              </button>
                              <span>·</span>
                              <button
                                type="button"
                                onClick={() => openParty(sellerId, `Seller ${sellerLabel}`)}
                                className="text-amber-400 hover:text-amber-300 hover:underline"
                                title={`Seller #${sellerId}`}
                              >
                                S: {sellerLabel}
                              </button>
                            </p>
                          </div>
                        </td>
                        <td className="table-cell text-sm">{reasonLabel(d.reason_code)}</td>
                        <td className="table-cell text-sm">
                          <span className="text-slate-300">{d.opener_role}</span>{' '}
                          <button
                            type="button"
                            onClick={() =>
                              openParty(d.opened_by_id, `${d.opener_role} ${openerLabel}`)
                            }
                            className="text-indigo-400 hover:text-indigo-300 hover:underline font-medium"
                            title={`#${d.opened_by_id}`}
                          >
                            {openerLabel}
                          </button>
                        </td>
                        <td className="table-cell">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityBadgeClass(d.priority)}`}>
                            {d.priority}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${statusBadgeClass(d.status)}`}>{d.status.replace('_', ' ')}</span>
                        </td>
                        <td className="table-cell text-slate-300">
                          {d.trade_total ?? '—'} {d.fiat_currency ?? ''}
                        </td>
                        <td className="table-cell text-slate-400 text-sm">
                          {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
                        </td>
                        <td className="table-cell text-right">
                          <Link
                            to={`${disputesBase}/${d.id}`}
                            className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                          >
                            Review
                          </Link>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>

              {data.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    {data.total.toLocaleString('en-US')} dispute{data.total === 1 ? '' : 's'}
                  </p>
                  <Pagination
                    currentPage={data.current_page}
                    lastPage={data.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <UserDetailModal
        isOpen={partyUserId !== null}
        onClose={() => setPartyUserId(null)}
        userId={partyUserId}
        title={partyTitle}
      />

      <Modal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        title={isOtc ? 'OTC dispute settings' : 'Dispute settings'}
        size="md"
      >
        {settingsLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
            Loading…
          </div>
        ) : (
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Dispute window after order creation
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Users can open a dispute on a trade only within this period after the order is
                created. Set 0 for no time limit.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={windowValue}
                  onChange={(e) => {
                    setSettingsBanner(null);
                    setWindowValue(e.target.value);
                  }}
                  className="input w-28"
                />
                <select
                  value={windowUnit}
                  onChange={(e) => {
                    setSettingsBanner(null);
                    setWindowUnit(e.target.value as WindowUnit);
                  }}
                  className="input w-32"
                >
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </select>
              </div>
              {currentWindowMinutes !== null && (
                <p className="text-xs text-slate-500 mt-2">
                  Current: {formatWindowLabel(currentWindowMinutes)}. Maximum 30 days. Applies only
                  to opening new disputes; already opened disputes are not affected.
                </p>
              )}
            </div>

            {settingsError && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {settingsError}
              </div>
            )}
            {settingsBanner && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                {settingsBanner}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsSettingsOpen(false)}
                className="btn btn-secondary py-2"
              >
                Close
              </button>
              <button type="submit" disabled={settingsSaving} className="btn btn-primary py-2">
                {settingsSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </Layout>
  );
};

export default DisputesPage;
